/**
 * Shared formatting utilities
 * Used across multiple components to avoid duplication
 */

/**
 * Format duration in seconds to "Xm Ys" display.
 * Examples: 55 -> "55s", 120 -> "2m", 125 -> "2m 5s"
 */
export const formatDuration = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

/**
 * Format question kind from snake_case to Title Case
 * Example: "coding_challenge" -> "Coding Challenge"
 */
export const formatQuestionKind = (kind: string | null | undefined): string => {
  if (!kind) return 'Question';
  return kind.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ');
};

/**
 * Format duration in seconds to "m:ss" display.
 * Examples: 6 -> "0:06", 65 -> "1:05", 180 -> "3:00"
 */
export const formatSecondsAsMss = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Format question timer display as "currentTime / totalTime"
 * Returns formatted string like "0:06 / 3:00" or "— / 3:00" if values are missing
 */
export const formatQuestionTimerDisplay = (
  timeSpentSeconds: number | null | undefined,
  timeAllocatedMinutes: number | null | undefined
): string => {
  // Cap spent at allocated: backend occasionally reports end timestamps past the
  // question deadline, which would otherwise surface as a confusing overrun.
  const cappedSpent = timeSpentSeconds != null && timeAllocatedMinutes != null
    ? Math.min(timeSpentSeconds, timeAllocatedMinutes * 60)
    : timeSpentSeconds;
  const currentTime = cappedSpent != null ? formatSecondsAsMss(cappedSpent) : '--:--';
  const totalTime = timeAllocatedMinutes != null ? formatSecondsAsMss(timeAllocatedMinutes * 60) : '--:--';
  return `${currentTime} / ${totalTime}`;
};

/**
 * Format question timer title for accessibility
 */
export const formatQuestionTimerTitle = (
  timeSpentSeconds: number | null | undefined,
  timeAllocatedMinutes: number | null | undefined
): string => {
  if (timeSpentSeconds != null && timeAllocatedMinutes != null) {
    const cappedSpent = Math.min(timeSpentSeconds, timeAllocatedMinutes * 60);
    return `${formatDuration(cappedSpent)} spent / ${timeAllocatedMinutes}m allocated`;
  }
  if (timeSpentSeconds != null) {
    return `${formatDuration(timeSpentSeconds)} spent`;
  }
  return `${timeAllocatedMinutes}m allocated`;
};

